import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { StyledProps } from '../types/styled-utils';

const FeaturesSection = styled.section`
  padding: 6rem 2rem;
  position: relative;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    padding: 4rem 1rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-align: center;
  
  span {
    background: linear-gradient(
      to right,
      ${(props: StyledProps) => props.theme.colors.gradientStart},
      ${(props: StyledProps) => props.theme.colors.gradientEnd}
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-fill-color: transparent;
  }
`;

const SectionSubheading = styled.p`
  font-size: 1.2rem;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  color: ${(props: StyledProps) => props.theme.colors.secondaryText};
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const FeatureCard = styled(motion.div)`
  background: rgba(18, 20, 37, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 6px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(3px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
    border-color: rgba(110, 86, 207, 0.2);
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(110, 86, 207, 0.07);
  border-radius: 50%;
  margin-bottom: 1.5rem;
  color: ${(props: StyledProps) => props.theme.colors.primary};
  font-size: 1.8rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
`;

const FeatureDescription = styled.p`
  color: ${(props: StyledProps) => props.theme.colors.secondaryText};
  line-height: 1.6;
  margin-bottom: 1rem;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
`;

const FeatureList = styled.ul`
  margin-top: 1rem;
  padding-left: 1.5rem;
  
  li {
    margin-bottom: 0.5rem;
    color: ${(props: StyledProps) => props.theme.colors.secondaryText};
    position: relative;
    text-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
    
    &::before {
      content: "•";
      color: ${(props: StyledProps) => props.theme.colors.primary};
      position: absolute;
      left: -1.2rem;
      font-size: 1.2rem;
    }
  }
`;

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.1 * i,
      duration: 0.5,
    }
  })
};

const Features: React.FC = () => {
  const features = [
    {
      icon: '🎵',
      title: 'Music Production',
      description: 'Creating electronic music with a focus on innovative sound design and immersive atmospheres.',
      list: [
        'Electronic music production',
        'Sound design & synthesis',
        'Audio engineering',
        'Composition & arrangement'
      ]
    },
    {
      icon: '💻',
      title: 'Web Development',
      description: 'Building modern, responsive web applications with cutting-edge technologies.',
      list: [
        'React & TypeScript',
        'Modern UI/UX design',
        'Interactive experiences',
        'Performance optimization'
      ]
    },
    {
      icon: '🔮',
      title: 'Creative Coding',
      description: 'Exploring the intersection of code and creativity through interactive art and visualizations.',
      list: [
        'Audio visualization',
        'Generative art',
        'Interactive installations',
        'Experimental interfaces'
      ]
    }
  ];
  
  return (
    <FeaturesSection id="about">
      <SectionHeading>
        About <span>6bit</span>
      </SectionHeading>
      <SectionSubheading>
        Music producer and developer passionate about creating digital experiences.
      </SectionSubheading>
      
      <FeatureGrid>
        {features.map((feature, index) => (
          <FeatureCard
            key={index}
            custom={index}
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            <FeatureIcon>{feature.icon}</FeatureIcon>
            <FeatureTitle>{feature.title}</FeatureTitle>
            <FeatureDescription>{feature.description}</FeatureDescription>
            <FeatureList>
              {feature.list.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </FeatureList>
          </FeatureCard>
        ))}
      </FeatureGrid>
    </FeaturesSection>
  );
};

export default Features; 